import { Routes } from '@angular/router';
import { authGuard }              from './core/guards/auth.guard';
import { roleGuard }              from './core/guards/role.guard';
import { alreadyLoggedOwnerGuard } from './core/guards/already-logged.guard';
import { storeSelectedGuard }     from './core/guards/store-selected.guard';
import { permissionGuard }        from './core/guards/permission.guard';
import { onboardingGuard }        from './core/guards/onboarding.guard';
import { packageRequiredGuard }   from './core/guards/package-required.guard';
import { adminAuthGuard }         from './core/guards/admin-auth.guard';
import { alreadyLoggedAdminGuard } from './core/guards/already-logged-admin.guard';
import { committeeAuthGuard }      from './core/guards/committee-auth.guard';

export const routes: Routes = [

  // ── Layout Main (Public) ──────────────────────────────────────────────────
  {
    path: '',
    loadComponent: () => import('./layouts/layout-main/layout-main').then(m => m.LayoutMain),
    children: [
      {
        path: '',
        loadComponent: () => import('./features/public/landing/landing.page').then(m => m.LandingPage),
      },
      {
        path: 'owners',
        loadComponent: () => import('./features/public/owners/owners.page').then(m => m.OwnersPage),
      },
      {
        path: 'owners/:slug',
        loadComponent: () => import('./features/public/owner-detail/owner-detail.page').then(m => m.OwnerDetailPage),
      },
      {
        path: 'owners/:ownerId/auction/:auctionId',
        loadComponent: () => import('./features/public/auction-detail/auction-detail.page').then(m => m.AuctionDetailPage),
      },
      {
        path: 'projects/:id',
        loadComponent: () => import('./features/public/project-detail/project-detail.page').then(m => m.PublicProjectDetailPage),
      },
      {
        path: 'terms-and-conditions',
        loadComponent: () => import('./features/public/legal/terms.page').then(m => m.TermsPage),
      },
      {
        path: 'privacy-policy',
        loadComponent: () => import('./features/public/legal/privacy.page').then(m => m.PrivacyPage),
      },
    ],
  },

  // ── Magic link ยืนยันบัญชี (เชิญสมาชิก/กรรมการเข้าร้าน — login อัตโนมัติ) ──────────
  {
    path: 'member/activate',
    loadComponent: () => import('./features/member-activate/member-activate.page').then(m => m.MemberActivatePage),
  },

  // ── Committee zone (กรรมการภายนอกที่ถูกเชิญทางอีเมล — ล็อกอินแบบเบา) ──────────
  {
    path: 'committee',
    children: [
      { path: '', redirectTo: 'claim', pathMatch: 'full' },
      // public: claim + OTP (เข้าจากลิงก์ในอีเมล)
      { path: 'claim', loadComponent: () => import('./features/committee/claim/claim.page').then(m => m.CommitteeClaimPage) },
      // protected: ต้องมี session token
      { path: 'review', canActivate: [committeeAuthGuard], loadComponent: () => import('./features/committee/review/review.page').then(m => m.CommitteeReviewPage) },
    ],
  },

  // ── Layout Admin (Step 2: isolated session + protected zone) ──────────────
  {
    path: 'admin',
    children: [
      { path: '', redirectTo: 'account', pathMatch: 'full' },

      // Login zone: ถ้า admin login อยู่แล้วให้เด้งไป /admin/users
      {
        path: 'account',
        canActivate: [alreadyLoggedAdminGuard],
        loadComponent: () => import('./layouts/layout-admin-account/layout-admin-account').then(m => m.LayoutAdminAccount),
        children: [
          { path: '', loadComponent: () => import('./features/admin-account/account/account.page').then(m => m.AdminAccountPage) },
        ],
      },

      // Protected zone: ต้องมี admin token ที่ยังไม่หมดอายุ
      {
        path: '',
        canActivate: [adminAuthGuard],
        loadComponent: () => import('./layouts/layout-admin-auth/layout-admin-auth').then(m => m.LayoutAdminAuth),
        children: [
          { path: 'users',          loadComponent: () => import('./features/admin/users/users.page').then(m => m.AdminUsersPage) },
          { path: 'users/:userId',  loadComponent: () => import('./features/admin/users/user-detail.page').then(m => m.AdminUserDetailPage) },
          { path: 'stores',         loadComponent: () => import('./features/admin/stores/stores.page').then(m => m.AdminStoresPage) },
          { path: 'reports',        loadComponent: () => import('./features/admin/reports/reports-list.page').then(m => m.AdminReportsListPage) },
          { path: 'reports/:id',    loadComponent: () => import('./features/admin/reports/report-detail.page').then(m => m.AdminReportDetailPage) },
          { path: 'kyc',            loadComponent: () => import('./features/admin/kyc/kyc-list.page').then(m => m.AdminKycListPage) },
          { path: 'kyc/:slug',      loadComponent: () => import('./features/admin/kyc/kyc-detail.page').then(m => m.AdminKycDetailPage) },
          { path: 'system',         loadComponent: () => import('./features/admin/system/system.page').then(m => m.AdminSystemPage) },
          { path: 'add-usage-days', loadComponent: () => import('./features/admin/add-usage-days/add-usage-days.page').then(m => m.AdminAddUsageDaysPage) },
        ],
      },
    ],
  },

  // ── Layout Bidder (Auth: bidder) ──────────────────────────────────────────
  {
    path: 'bidder',
    loadComponent: () => import('./layouts/layout-bidder/layout-bidder').then(m => m.LayoutBidder),
    canActivate: [authGuard, roleGuard],
    data: { role: 'bidder' },
    children: [
      { path: 'my-auction', redirectTo: 'bidding', pathMatch: 'full' },
      { path: 'bidding', loadComponent: () => import('./features/bidder/my-auction/my-auction.page').then(m => m.MyAuctionPage) },
      { path: 'bidding/:id', loadComponent: () => import('./features/bidder/auction-detail/bidder-auction-detail.page').then(m => m.BidderAuctionDetailPage) },
      { path: 'projects', loadComponent: () => import('./features/bidder/my-projects/my-projects.page').then(m => m.BidderMyProjectsPage) },
      { path: 'projects/:id', loadComponent: () => import('./features/bidder/my-projects/project-submit.page').then(m => m.BidderProjectSubmitPage) },
      { path: 'notifications', loadComponent: () => import('./features/bidder/notifications/notifications.page').then(m => m.BidderNotificationsPage) },
      { path: 'profile', loadComponent: () => import('./features/bidder/profile/profile.page').then(m => m.BidderProfilePage) },
      { path: '', redirectTo: 'bidding', pathMatch: 'full' },
    ],
  },

  // ── Layout Owner (single /owner parent — OQ-1 ✅ Option B) ───────────────
  {
    path: 'owner',
    children: [
      { path: '', redirectTo: 'account', pathMatch: 'full' },

      // ─── Entry Zone: LayoutOwnerAccount ──────────────────────────────────
      {
        path: 'account',
        loadComponent: () => import('./layouts/layout-owner-account/layout-owner-account').then(m => m.LayoutOwnerAccount),
        canActivate: [alreadyLoggedOwnerGuard],
        children: [
          { path: '', loadComponent: () => import('./features/owner-account/account/account.page').then(m => m.OwnerAccountPage) },
        ],
      },
      {
        path: 'start',
        loadComponent: () => import('./layouts/layout-owner-account/layout-owner-account').then(m => m.LayoutOwnerAccount),
        canActivate: [authGuard],
        children: [
          { path: '', loadComponent: () => import('./features/owner-account/start/start.page').then(m => m.OwnerStartPage) },
        ],
      },
      {
        path: 'select-store',
        loadComponent: () => import('./layouts/layout-owner-account/layout-owner-account').then(m => m.LayoutOwnerAccount),
        canActivate: [authGuard],
        children: [
          { path: '', loadComponent: () => import('./features/owner-account/select-store/select-store.page').then(m => m.SelectStorePage) },
        ],
      },

      // ─── Auth Zone: LayoutOwnerAuth ──────────────────────────────────────
      {
        path: '',
        loadComponent: () => import('./layouts/layout-owner-auth/layout-owner-auth').then(m => m.LayoutOwnerAuth),
        canActivate: [authGuard, roleGuard, storeSelectedGuard, onboardingGuard],
        data: { role: 'owner' },
        children: [
          { path: 'dashboard',  canActivate: [permissionGuard], data: { pageId: 'dashboard' }, loadComponent: () => import('./features/owner-auth/dashboard/dashboard.page').then(m => m.OwnerDashboardPage) },
          { path: 'list',       canActivate: [permissionGuard], data: { pageId: 'list' },      loadComponent: () => import('./features/owner-auth/list/list.page').then(m => m.OwnerListPage) },
          { path: 'detail',     canActivate: [permissionGuard], data: { pageId: 'detail' },    loadComponent: () => import('./features/owner-auth/detail/detail.page').then(m => m.OwnerDetailPage) },
          { path: 'form',       canActivate: [permissionGuard, packageRequiredGuard], data: { pageId: 'form' },      loadComponent: () => import('./features/owner-auth/form/form.page').then(m => m.OwnerFormPage) },
          { path: 'auction-project', canActivate: [permissionGuard], data: { pageId: 'auction-project' }, loadComponent: () => import('./features/owner-auth/auction-project/auction-project.page').then(m => m.OwnerAuctionProjectPage) },
          { path: 'review-inbox', loadComponent: () => import('./features/owner-auth/review-inbox/review-inbox.page').then(m => m.OwnerReviewInboxPage) },
          { path: 'drafts',     canActivate: [permissionGuard], data: { pageId: 'form' },      loadComponent: () => import('./features/owner-auth/drafts/drafts.page').then(m => m.OwnerDraftsPage) },
          { path: 'chat',       canActivate: [permissionGuard], data: { pageId: 'chat' },      loadComponent: () => import('./features/owner-auth/chat/chat.page').then(m => m.OwnerChatPage) },
          { path: 'user',       canActivate: [permissionGuard], data: { pageId: 'user' },      loadComponent: () => import('./features/owner-auth/user/user.page').then(m => m.OwnerUserPage) },
          { path: 'invite',     canActivate: [permissionGuard], data: { pageId: 'user' },      loadComponent: () => import('./features/owner-auth/invite/invite.page').then(m => m.OwnerInvitePage) },
          { path: 'bidder',          canActivate: [permissionGuard], data: { pageId: 'bidder' }, loadComponent: () => import('./features/owner-auth/bidder/bidder-list.page').then(m => m.OwnerBidderListPage) },
          { path: 'bidder/:userId',  canActivate: [permissionGuard], data: { pageId: 'bidder' }, loadComponent: () => import('./features/owner-auth/bidder/bidder-detail.page').then(m => m.OwnerBidderDetailPage) },
          { path: 'bidder-invite',   canActivate: [permissionGuard], data: { pageId: 'bidder' }, loadComponent: () => import('./features/owner-auth/bidder-invite/bidder-invite.page').then(m => m.OwnerBidderInvitePage) },
          { path: 'role',       canActivate: [permissionGuard], data: { pageId: 'role' },       loadComponent: () => import('./features/owner-auth/role/role.page').then(m => m.OwnerRolePage) },
          { path: 'permission', canActivate: [permissionGuard], data: { pageId: 'permission' }, loadComponent: () => import('./features/owner-auth/permission/permission.page').then(m => m.OwnerPermissionPage) },
          { path: 'company',    canActivate: [permissionGuard], data: { pageId: 'company' },    loadComponent: () => import('./features/owner-auth/company/company.page').then(m => m.OwnerCompanyPage) },
          // ── ปิด route /owner/integrations ชั่วคราว (System SMTP เป็น central — ไม่ต้องการ UI per-store) ──
          // { path: 'integrations', canActivate: [permissionGuard], data: { pageId: 'integrations' }, loadComponent: () => import('./features/owner-auth/integrations/integrations.page').then(m => m.OwnerIntegrationsPage) },
          { path: 'profile',    loadComponent: () => import('./features/owner-auth/profile/profile.page').then(m => m.OwnerAuthProfilePage) },
          { path: 'notifications', loadComponent: () => import('./features/owner-auth/notifications/notifications.page').then(m => m.OwnerNotificationsPage) },
          { path: 'packages',   loadComponent: () => import('./features/owner-auth/packages/packages.page').then(m => m.OwnerPackagesPage) },
          { path: 'invoices',   loadComponent: () => import('./features/owner-auth/invoices/invoices.page').then(m => m.OwnerInvoicesPage) },
          { path: 'reports',    canActivate: [permissionGuard], data: { pageId: 'issue-report' }, loadComponent: () => import('./features/owner-auth/reports/owner-reports.page').then(m => m.OwnerReportsPage) },
        ],
      },
    ],
  },

  // ── Error Pages ───────────────────────────────────────────────────────────
  { path: '403', loadComponent: () => import('./features/shared/forbidden/forbidden.page').then(m => m.ForbiddenPage) },
  { path: '404', loadComponent: () => import('./features/shared/not-found/not-found.page').then(m => m.NotFoundPage) },
];

